import type { Notification, NotificationFeed } from '@/types/domain'

/* The seam between the server's spelling of a notification and the client's.
 *
 * The bell has two mouths feeding one cache — `GET /api/notifications` and the
 * SSE stream — and both carry the same row out of `outbox_messages`. That row's
 * `kind` is the outbox TOPIC (`case.assigned`, `quote.sent`), not the small
 * vocabulary the panel renders, so the mapping has to happen on the way in from
 * BOTH of them. It used to live in useNotificationStream.ts alone, which was
 * correct only for as long as the feed query was reading a fixture that already
 * spoke the panel's vocabulary: the moment the real endpoint replaced it, an
 * unmapped `case.assigned` reached `KIND_ICONS[kind]` as `undefined` and the
 * drawer crashed on the first real row rather than rendering it plainly.
 *
 * So the conversion lives here, once, and both readers go through it — the same
 * arrangement, and for the same reason, as `features/cases/api/caseWire.ts`.
 */

/** One bell event as the server spells it (`collab.NotificationDTO`).
 *
 *  Identical to `Notification` bar two fields: `kind` is the topic string here
 *  and a closed union there, and `topic` does not exist here at all — the
 *  server never sends one, so the client fills it from `kind` on the way in.
 *  Declaring it here would be an optional field the server never sends, which
 *  every fixture would set and every real row would lack. Declared as
 *  `Omit<…>` rather than restated so a field added to the domain type cannot
 *  be silently dropped on the way in.
 *
 *  So `code` and `params` (story B5) arrive by that `Omit` and pass through
 *  `notificationFromWire` untouched: the server always sends both (`""` and
 *  `{}` for a row from before codes), and they are worded at render, never
 *  here, because the cache outlives a language switch. */
export interface NotificationWire extends Omit<Notification, 'kind' | 'topic'> {
  kind: string
}

/** The feed and its badge (`collab.FeedDTO`). The count is the server's, taken
 *  verbatim and never recomputed here.
 *
 *  It is counted over the same rows `items` carries — the reachable ones, after
 *  the server's read-time access re-check — so it is capped at the server's
 *  `FeedLimit` (100) exactly as the list is, and a reader with four hundred
 *  unread sees 100. Recounting here would agree with it today, which is the
 *  trap: the day the feed is paged, a client counting the first page starts
 *  under-reporting silently instead of failing. */
export interface NotificationFeedWire {
  items: NotificationWire[]
  unreadCount: number
}

/** Maps an outbox topic to the icon vocabulary the panel renders.
 *
 *  A topic is a CONTRACT (AD-22): the server sends it verbatim, the client maps
 *  it to an icon and an i18n key, and renaming one is a breaking change rather
 *  than a refactor. The panel's own vocabulary is smaller and is about how a row
 *  LOOKS. Mapping here rather than widening `NotificationKind` keeps that union
 *  closed, so a component switching on it still gets exhaustiveness from
 *  TypeScript, and so a topic the server invents tomorrow needs no type change
 *  to be displayable.
 *
 *  An unknown topic falls back to 'status' rather than being dropped. A new
 *  server event appearing as a generic row is a cosmetic gap; a new server event
 *  vanishing from the bell is somebody never learning they were assigned. */
export function kindForTopic(topic: string): Notification['kind'] {
  switch (topic) {
    /* 'quote.participant-added' shares this arm: a party joining a quote reads
       as an assignment rather than a status, because the row says somebody was
       PUT ON a record, which is the same shape as being assigned to a case even
       though the party is external and the reader is the quote's owner. Letting
       it reach 'status' via the default would work, but the icon would say
       "something changed" about an event that is specifically about a person.

       'case.participant-assigned' is the email-channel twin of that (FR-48) and
       lands on the same arm: the bell never shows it today — the recipient is an
       external party with no login — but the stream and the feed are one code
       path, and an arm that is right if the row ever arrives costs nothing.

       'quote.team-assigned' is the internal half of that: a colleague was put
       on a quote, which is the same row shape as being assigned to a case and
       differs only in which record it names. Note what does NOT share this arm
       — 'quote.pricing-requested' below, which is the same act with the other
       meaning.

       The note sits above all four labels rather than between them: a comment
       BETWEEN two case labels makes the first one non-empty to no-fallthrough,
       which then reads a deliberate shared arm as a missing `break`. */
    case 'case.assigned':
    case 'case.participant-assigned':
    case 'quote.participant-added':
    case 'quote.team-assigned':
      return 'assignment'
    case 'case.stuck':
      return 'stuck'
    /* Chat, on either record kind. `quote.message` was missing here while the
       feed was a fixture and nothing noticed: it fell through to 'status', so a
       client's reply on a quote arrived under a tick rather than a speech
       bubble. */
    case 'case.message':
    case 'quote.message':
      return 'message'
    case 'case.file':
      return 'file'
    /* A cost line recorded, amended or dropped is the case's COSTS changing, so
       it has a kind of its own (N-28). It was filed under 'payment' and read
       «Платёж» over a row that says "Expense line recorded": no money moved, a
       figure on the cost sheet did. The reader's question about this row is
       "what did it cost", which is the receipt's, not the wallet's. */
    case 'case.expense-changed':
      return 'expense'
    /* Money that moved, or is about to. 'finance.payment-awaits-confirmation'
       (QW-10) is a carrier payment recorded and waiting for Finance to confirm
       it: a payment row first and a decision second. It joined the approval arm
       until N-28 because its reader has something to DO with it, and read
       «Согласование» over a payment; what the row is ABOUT is the payment, and
       the sidebar's Finance badge is what tells the confirmer it is theirs. */
    case 'finance.payment':
    case 'finance.payment-awaits-confirmation':
      return 'payment'
    /* The price request's whole life on a quote (S-2a): 'quote.pricing-requested'
       is somebody waiting for a price, 'quote.pricing-ready' is the answer and
       'quote.pricing-reopened' is the ask coming back because an edit moved the
       price. They are NOT 'approval' (N-28) — nobody is asked to approve
       anything, so «Согласование» over «A price is wanted on this quote» named
       the wrong act — and not 'assignment', which is what 'quote.team-assigned'
       above already says. A kind of its own keeps the topic the server minted
       separately worth having: the word beside each row is the request, not a
       status tick and not a staffing. */
    case 'quote.pricing-requested':
    case 'quote.pricing-ready':
    case 'quote.pricing-reopened':
      return 'pricing'
    /* A decision asked for, and a decision given. A rate change is money, but
       the SHAPE of both rows is an approval — somebody is being asked, or has
       answered — which is what the gavel says and the wallet does not. The
       client's answer on a quote is the same shape from outside the company.
       'quote.approval' and 'finance.approval' are not in the server's registry
       today; they keep this arm for a topic of that shape arriving later. */
    case 'quote.answered':
    case 'quote.approval':
    case 'finance.approval':
    case 'finance.rate-change-requested':
    case 'finance.rate-change-decided':
      return 'approval'
    /* Something left the building as a document: the offer to the client, and
       whatever the document module issues. */
    case 'quote.sent':
    case 'document.issued':
      return 'document'
    /* The remaining contract topics — 'case.status', 'case.status-sent',
       'quote.converted' — are genuine status transitions and are served by the
       default arm. They are named here rather than given empty arms of their
       own: an arm returning what the default already returns is a line that can
       drift from its own comment. */
    default:
      return 'status'
  }
}

/** One event, server spelling to client spelling. The raw topic is kept as
 *  `topic`, because `kind` is about the icon and folds topics together. */
export function notificationFromWire(wire: NotificationWire): Notification {
  return { ...wire, kind: kindForTopic(wire.kind), topic: wire.kind }
}

/** Whether a decoded value is shaped like a bell event at all.
 *
 *  `JSON.parse` guarantees syntax and nothing else: `{"foo":1}` parses happily
 *  and a cast to `NotificationWire` makes TypeScript believe it. What reaches
 *  the cache then is a row whose `id` and `href` are `undefined`, and the drawer
 *  renders `<Link to={undefined}>`, which react-router throws on — a malformed
 *  frame from the server takes the whole panel down rather than being ignored.
 *
 *  Two fields are checked and no more, deliberately. `id` is what the merge
 *  deduplicates on, so a row without one is a row that multiplies on every
 *  redelivery; `href` is what the panel navigates by and is the field that
 *  actually crashes. Everything else renders as an empty string, which is a
 *  cosmetic gap in one row rather than a broken bell — and a full validator here
 *  would be a second copy of the server's payload contract, drifting quietly. */
export function isNotificationWire(value: unknown): value is NotificationWire {
  if (typeof value !== 'object' || value === null) return false

  const row = value as Record<string, unknown>
  return typeof row.id === 'string' && row.id !== '' && typeof row.href === 'string'
}

/** The whole feed, server spelling to client spelling. */
export function feedFromWire(wire: NotificationFeedWire): NotificationFeed {
  return {
    items: wire.items.map(notificationFromWire),
    unreadCount: wire.unreadCount,
  }
}
